/**
 * i18n Provider — Stockpilot Web
 *
 * Wraps @lingui/core directly (no community fork dependency) with a
 * SolidJS-native reactive layer. Locale changes update a Signal, which
 * causes all useLingui() consumers to re-derive translations reactively
 * without any component re-render overhead.
 *
 * Lazy-loading: Only the active locale's JS catalog is fetched at startup
 * (and when the user switches language), keeping the initial bundle minimal.
 *
 * DB-sync: Reads the persisted locale from PowerSync (users table) on mount
 * and writes back via updateUserLocaleMutation when the user changes language.
 *
 * RTL support: Sets document.documentElement.dir automatically for ar / he.
 */

import { i18n, type MessageDescriptor } from "@lingui/core";
import { generateMessageId } from "@lingui/message-utils/generateMessageId";
import {
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  type ParentComponent,
  useContext,
} from "solid-js";
import { RTL_LOCALES, SUPPORTED_LOCALES, type SupportedLocale } from "@stockpilot/config/locales";
import { queryKeys, type Session, updateUserLocaleMutation, VISITOR_ID } from "@stockpilot/shared";
import { queryClient } from "@/lib/query-client";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useUserPreferences } from "@/providers/user-preferences-context";

// Initialize with a default locale so that early translation calls do not throw errors
i18n.load("en", {});
i18n.activate("en");

// ─── Types ────────────────────────────────────────────────────────────────────

interface I18nContextValue {
  /** Reactive current locale code */
  locale: () => SupportedLocale;
  /** Change locale — updates DB + reloads catalog */
  setLocale: (locale: SupportedLocale) => Promise<void>;
  /** True while the catalog is loading */
  isLoading: () => boolean;
  /**
   * Reactive translation function.
   *
   * TYPE BARRIER (intentionally narrower than the implementation): it only accepts
   * `MessageDescriptor`, i.e. the result of a macro (`` msg`…` ``). A plain string
   * —`t("My Day")`— **does not compile**, and that is the important part: the Lingui extractor only sees
   * macros, so a plain string never enters the catalog and remains in English in
   * non-English locales WITHOUT any gate saying so. The implementation (`translate`)
   * still accepts a string and resolves it by hash, so nothing breaks at runtime; the
   * type makes it impossible to write.
   */
  t: (message: MessageDescriptor, values?: Record<string, unknown>) => string;
  /**
   * Translates a DYNAMIC key: a string unknown at the call site because
   * it comes from a data table or function (`getApiStatusLabelKey(…)`).
   *
   * It is correct only if that exact text is declared with `` msg`…` `` in a MANIFEST
   * that the extractor can see (e.g. `lib/connectivity/messages.ts`), because then the
   * id hash matches and the catalog resolves it. Without a manifest, the string is NOT extracted and the
   * user sees it in English in non-English locales.
   *
   * This exists so the escape from the strict `t` type is EXPLICIT and greppable, instead of
   * a silent `as`. Every use must point to its manifest.
   */
  tDynamic: (message: string, values?: Record<string, unknown>) => string;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const I18nContext = createContext<I18nContextValue>();

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
}

// ─── Catalog Loader ───────────────────────────────────────────────────────────

interface CatalogModule {
  messages: Record<string, string>;
}

function assertCatalogModule(mod: unknown): asserts mod is CatalogModule {
  if (typeof mod !== "object" || mod === null || !("messages" in mod)) {
    throw new Error("Invalid catalog module");
  }
}

/** An already-imported catalog, ready to be handed to i18n. */
interface FetchedCatalog {
  /** The language that could ACTUALLY be loaded (== requested, except fallback to "en"). */
  locale: SupportedLocale;
  messages: Record<string, string>;
}

/**
 * Dynamically imports the compiled .js catalog for the requested locale.
 * Vite will code-split each locale into a separate chunk automatically.
 *
 * ⚠️ This function is PURE with respect to i18n: it imports and returns, but does NOT call
 * `i18n.load`/`i18n.activate`. It used to, and that was the bug: `applyToken`
 * in `applyLocale` is checked only AFTER the await, so a stale
 * load had already activated its language before the guard could
 * discard it. `applyLocale` decides whether the result is still current, and
 * for that the side effect must happen AFTER that check.
 */
async function fetchCatalog(locale: SupportedLocale): Promise<FetchedCatalog> {
  try {
    const rawModule: unknown = await import(`../../locales/${locale}/messages.mjs`);
    assertCatalogModule(rawModule);
    return { locale, messages: rawModule.messages };
  } catch (err) {
    console.warn(`[i18n] Failed to load catalog for "${locale}", falling back to "en"`, err);
    // Fall back to English if catalog missing
    const rawEnModule: unknown = await import("../../locales/en/messages.mjs");
    assertCatalogModule(rawEnModule);
    return { locale: "en", messages: rawEnModule.messages };
  }
}

/** Applies correct RTL/LTR direction on <html> element */
function applyDirection(locale: SupportedLocale) {
  document.documentElement.dir = RTL_LOCALES.has(locale) ? "rtl" : "ltr";
  document.documentElement.lang = locale;
}

/** Clean type-guard to verify if an unknown string represents a SupportedLocale */
function isSupportedLocale(locale: unknown): locale is SupportedLocale {
  return typeof locale === "string" && SUPPORTED_LOCALES.some((code) => code === locale);
}

/** Detect browser locale and match against supported locales */
function detectBrowserLocale(): SupportedLocale {
  const browser = navigator.language.split("-")[0]?.toLowerCase() ?? "en";
  return isSupportedLocale(browser) ? browser : "en";
}

/**
 * Per-account localStorage key for the cached locale (multi-account #18).
 *
 * The language belongs to the ACTIVE account of THIS tab — each real account AND
 * the guest keep their own. Keying the cache by userId means a language NEVER
 * leaks across accounts/guest or between tabs.
 */
function cacheKey(userId: string): string {
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- localStorage key, not UI copy
  return `@app_locale:${userId}`;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export const I18nProvider: ParentComponent = (props) => {
  const drizzle = useDrizzle();
  const session = useSession();
  const { preferences } = useUserPreferences();

  const [locale, setLocaleSignal] = createSignal<SupportedLocale>("en");
  const [isLoading, setIsLoading] = createSignal(true);
  // Bumped after every REAL catalog load. Consumers (`translate`) depend on it so
  // they re-derive when a catalog finishes loading even if the `locale` signal
  // value didn't change (e.g. the very first load of the default "en", which
  // equals the signal's initial value → `setLocaleSignal` would be a no-op).
  const [catalogVersion, setCatalogVersion] = createSignal(0);

  // Per-account "pending local change": after the user picks a language we hold it
  // until that account's synced `user.locale` catches up, so the async DB round-trip
  // can't revert the fresh choice. Keyed by userId (the guest has its own entry).
  const pending = new Map<string, SupportedLocale>();

  const readCachedLocale = (userId: string): SupportedLocale | null => {
    try {
      const v = localStorage.getItem(cacheKey(userId));
      return isSupportedLocale(v) ? v : null;
    } catch {
      return null;
    }
  };

  // The effect can fire several times in quick succession during boot/account
  // switches (URL transitions), each starting an async `loadCatalog`. `applyToken`
  // makes only the most recent request commit ("last write wins").
  let applyToken = 0;
  // Unmounting invalidates any in-flight `applyLocale`: its catalog import can land after the
  // provider is gone, and `i18n.activate` would then run against a torn-down tree.
  onCleanup(() => {
    applyToken++;
  });

  // Locales whose COMPILED catalog has actually been loaded into i18n. The module
  // init does `i18n.load("en", {})` with an EMPTY placeholder catalog purely so
  // early translation calls don't throw — that is NOT a real load. Gating the
  // (re)load on the locale CHANGING (`next !== locale()`) skipped loading the real
  // catalog whenever the resolved locale equalled the "en" signal default, leaving
  // the EMPTY en catalog in place: in dev the lingui macro inlines each message's
  // default text so strings still rendered, but in a PRODUCTION build the macro
  // strips those defaults, so every string rendered as its raw hash id (the whole
  // UI became unusable for English users). Track real loads explicitly so the FIRST
  // apply of ANY locale — including the default "en" — loads its catalog once.
  const loadedLocales = new Set<SupportedLocale>();

  const applyLocale = async (next: SupportedLocale): Promise<void> => {
    const token = ++applyToken;
    // Load the catalog the first time we see a locale, but ALWAYS (re)apply
    // <html lang/dir> for the active account. The token guard ensures ONLY the most
    // recent request mutates anything, so a transient/stale apply during a switch
    // can't clobber the settled account's locale.
    try {
      if (loadedLocales.has(next)) {
        if (token !== applyToken) {
          return; // superseded
        }
        // The catalog is already IN MEMORY, but `i18n` remains activated in the
        // previous language: `loadCatalog` is what calls `i18n.activate()`, and we are
        // skipping it here. Without this re-activate, returning to an already visited
        // language (en → es → en) left the whole UI in the previous language while
        // `<html lang>` did change — a visible inconsistency and an a11y trap.
        i18n.activate(next);
        setCatalogVersion((v) => v + 1);
      } else {
        setIsLoading(true);
        const fetched = await fetchCatalog(next); // import only: does not touch i18n
        if (token !== applyToken) {
          return; // superseded by a newer request (it owns isLoading now)
        }
        i18n.load(fetched.locale, fetched.messages);
        i18n.activate(fetched.locale);
        loadedLocales.add(fetched.locale);
        // Force consumers to re-derive now that the real catalog is live, even if
        // the `locale` signal value is unchanged (first "en" load == default).
        setCatalogVersion((v) => v + 1);
      }
      setLocaleSignal(next);
      applyDirection(next);
    } finally {
      if (token === applyToken) {
        setIsLoading(false);
      }
    }
  };

  /** User-initiated change (the /language screen): apply + persist for the ACTIVE account. */
  const setLocale = async (newLocale: SupportedLocale): Promise<void> => {
    const userId = session.userId();
    pending.set(userId, newLocale);
    if (newLocale !== locale()) {
      await applyLocale(newLocale);
    }
    // Per-account cache (instant, offline, no cross-account leak).
    try {
      localStorage.setItem(cacheKey(userId), newLocale);
    } catch (err) {
      console.warn("[i18n] Could not persist locale to localStorage:", err);
    }
    // Keep the session query cache coherent.
    try {
      queryClient.setQueryData<Session>(queryKeys.auth.session(), (old) =>
        old ? { ...old, user: { ...old.user, locale: newLocale } } : old,
      );
    } catch (err) {
      console.warn("[i18n] Could not update session cache:", err);
    }
    // Real accounts persist to their own synced user row; the guest is local-only.
    const dbInstance = drizzle();
    if (dbInstance && userId !== VISITOR_ID) {
      try {
        await updateUserLocaleMutation(dbInstance, userId, newLocale);
      } catch (err) {
        console.warn("[i18n] Could not persist locale to DB:", err);
      }
    }
  };

  // Resolve + apply the locale for the ACTIVE account of this tab. Re-runs when the
  // active account changes (switch / per-tab nav) or when its synced locale changes,
  // so each account/guest shows its own language with no leak. Reads `locale()` via
  // `untrack` inside applyLocale so applying never re-triggers this effect.
  createEffect(() => {
    const userId = session.userId();
    // The guest has NO synced preferences. During a real→guest switch
    // `session.userId()` flips to the guest before the preferences query catches
    // up, so `preferences()` can transiently still hold the PREVIOUS real
    // account's locale. Trusting it would leak that locale to the guest, so the
    // guest ignores `preferences()` entirely and resolves from its own cache/browser.
    const isGuest = userId === VISITOR_ID;
    const remote = isGuest ? undefined : preferences()?.locale;
    const remoteOk = isSupportedLocale(remote) ? remote : null;

    // While a local change is pending for this account, keep it until the DB confirms.
    const pendingLocale = pending.get(userId);
    if (pendingLocale) {
      if (remoteOk === pendingLocale) {
        pending.delete(userId);
      }
      void applyLocale(pendingLocale);
      return;
    }

    // Source of truth: the account's synced locale; fall back to its per-account
    // cache (instant boot) then the browser. The guest has no synced locale → cache.
    const desired: SupportedLocale = remoteOk ?? readCachedLocale(userId) ?? detectBrowserLocale();
    void applyLocale(desired);
    // Mirror a real account's synced locale into its per-account cache (instant boot).
    if (remoteOk) {
      try {
        localStorage.setItem(cacheKey(userId), remoteOk);
      } catch {
        // localStorage unavailable — ignore.
      }
    }
  });

  const translate = (
    message: string | MessageDescriptor,
    values?: Record<string, unknown>,
  ): string => {
    locale(); // Establish dynamic reactive dependency on SolidJS signal
    catalogVersion(); // Re-derive when a catalog finishes loading (see applyLocale)
    if (typeof message === "string") {
      // The compiled catalog uses HASH ids (generateMessageId), just like the macro
      // `msg`...``. A plain string (`t("My Day")`) would look for id="My Day" and not
      // resolve (it would show English in any language). We compute the same id
      // as the macro so `t("literal")` translates the same as `t(msg`literal`)`,
      // with the text itself as fallback. (Fix for the "language not reflected" bug.)
      return i18n._({ id: String(generateMessageId(message)), message, values });
    }
    // Values also apply to descriptors (they go INSIDE the descriptor in
    // Lingui's API): without them, messages like msg`...{{date}}...`
    // rendered an empty placeholder ("{}"). The descriptor is built in a
    // separate variable: Lingui's extractor cannot parse an inline spread
    // inside `i18n._()` and aborts extraction of the file.
    const descriptor: MessageDescriptor = {
      ...message,
      values: { ...message.values, ...values },
    };
    return i18n._(descriptor);
  };

  return (
    <I18nContext.Provider
      value={{ locale, setLocale, isLoading, t: translate, tDynamic: translate }}
    >
      {/*
        Reactivity is provided by the locale() signal read inside translate();
        the app uses useI18n().t exclusively (no @lingui-solid/solid <Trans>/
        useLingui macros), so no extra runtime provider wrapper is needed.
      */}
      {props.children}
    </I18nContext.Provider>
  );
};
